import { useEffect, useRef, useState, type FormEvent } from 'react'
import Seo from '../../components/Seo'
import { api } from '../../api/client'
import { brand } from '../../brand'
import { contactLimits, validateContact } from './contactValidation'

type Status = 'idle' | 'sending' | 'sent' | 'error'

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const startedAt = useRef<number>(Date.now())
  const requestKey = useRef(crypto.randomUUID())
  const sending = useRef(false)
  const submittedOnce = useRef(false)

  useEffect(() => {
    startedAt.current = Date.now()
  }, [])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (sending.current || submittedOnce.current) return
    const form = new FormData(e.currentTarget)
    const data = {
      name: String(form.get('name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      company: String(form.get('company') ?? '').trim(),
      need: String(form.get('need') ?? '').trim(),
      website: String(form.get('website') ?? ''), // honeypot: real users leave it empty
      started_at: startedAt.current,
      idempotency_key: requestKey.current,
    }

    const next = validateContact(data)
    setErrors(next)
    if (Object.keys(next).length) return

    sending.current = true
    setStatus('sending')
    setServerError(null)
    try {
      await api.submitInquiry(data)
      submittedOnce.current = true
      setStatus('sent')
    } catch (err) {
      setStatus('error')
      setServerError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally { sending.current = false }
  }

  return (
    <>
      <Seo title="Contact" />
      <section className="hero">
        <span className="eyebrow">Contact</span>
        <h1 style={{ marginTop: '0.75rem' }}>Speak with us</h1>
        <p className="lede">
          Tell us about the categories in scope, the scale of spend, and your timeline. Your request goes to the Ledgerline team’s inbox.
        </p>
      </section>

      <section className="section">
        {status === 'sent' ? (
          <div className="notice" role="status">
            <strong>Received.</strong> Your request has been saved for the Ledgerline team.
          </div>
        ) : (
          <form className="form" onSubmit={onSubmit} noValidate>
            <div className="field">
              <label htmlFor="name">Name</label>
              <input id="name" name="name" maxLength={contactLimits.name} className="input" autoComplete="name" required aria-invalid={!!errors.name} />
              {errors.name && <span className="error">{errors.name}</span>}
            </div>
            <div className="field">
              <label htmlFor="email">Work email</label>
              <input id="email" name="email" maxLength={contactLimits.email} type="email" className="input" autoComplete="email" required aria-invalid={!!errors.email} />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>
            <div className="field">
              <label htmlFor="company">Company</label>
              <input id="company" name="company" maxLength={contactLimits.company} className="input" autoComplete="organization" required aria-invalid={!!errors.company} />
              {errors.company && <span className="error">{errors.company}</span>}
            </div>
            <div className="field">
              <label htmlFor="need">What would you like to discuss?</label>
              <textarea id="need" name="need" maxLength={contactLimits.need} className="textarea" required aria-invalid={!!errors.need} />
              <span className="hint">Categories, approximate annual spend, geography, and timing.</span>
              {errors.need && <span className="error">{errors.need}</span>}
            </div>
            <div className="field" aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
              <label htmlFor="website">Website</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" />
            </div>
            {status === 'error' && (
              <div className="notice crit" role="alert">
                We could not send your request{serverError ? `: ${serverError}` : ''}. Please try again
                {' '}
                or email <a href={`mailto:${brand.contactEmail}`}>{brand.contactEmail}</a>.
              </div>
            )}
            <div>
              <button type="submit" className="btn" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send request'}
              </button>
            </div>
            <p className="form-note">
              These details help us review and respond to your request. Suppliers responding to our outreach should reply
              to the email they received rather than use this form.
            </p>
          </form>
        )}
      </section>

      <section className="section">
        <dl className="deflist">
          <dt>Email</dt>
          <dd>
            <a href={`mailto:${brand.contactEmail}`}>{brand.contactEmail}</a>
          </dd>
          <dt>Post</dt>
          <dd>{brand.mailingAddress}</dd>
        </dl>
      </section>
    </>
  )
}
